import { audioPrefs } from "../audio/audioPrefs.js";
import { radio } from "../radio/radio.js";
import { store } from "../store/GameStore.js";
import { button, el } from "./dom.js";
import { bindPanelFocus, describePlayback, volumeControl } from "./radioWidgets.js";

/**
 * The HUD's now-playing chip, visible to everyone while the radio is on, and
 * its popover with the personal volume and local recovery actions. Changing
 * station still means walking to the hi-fi.
 */
export class RadioChip {
  readonly element: HTMLButtonElement;
  private readonly label: HTMLElement;
  private readonly icon: HTMLElement;
  private readonly popover: HTMLElement;
  private readonly popName: HTMLElement;
  private readonly popBy: HTMLElement;
  private readonly popStatus: HTMLElement;
  private readonly popAction: HTMLButtonElement;

  constructor(popoverParent: HTMLElement) {
    this.element = button("", "hud-btn radio-chip hidden");
    this.element.setAttribute("aria-haspopup", "dialog");
    this.element.setAttribute("aria-expanded", "false");
    this.label = el("span", "radio-chip-name");
    this.icon = el("span", "radio-chip-icon");
    this.icon.setAttribute("aria-hidden", "true");
    this.element.append(this.label, this.icon);
    this.element.addEventListener("click", () => this.toggle());

    this.popover = el("div", "panel radio-popover hidden");
    this.popover.setAttribute("role", "dialog");
    this.popover.setAttribute("aria-label", "Radio");
    this.popName = el("div", "radio-now-name");
    this.popBy = el("div", "radio-now-by");
    this.popStatus = el("div", "radio-status");
    this.popStatus.setAttribute("role", "status");
    this.popStatus.setAttribute("aria-live", "polite");
    this.popAction = button("", "radio-main-btn hidden");
    this.popAction.addEventListener("click", () => radio.resume());
    const hint = el("div", "radio-hint", "Walk up to the hi-fi to change station.");
    this.popover.append(
      this.popName,
      this.popBy,
      this.popStatus,
      this.popAction,
      volumeControl(),
      hint,
    );
    popoverParent.appendChild(this.popover);
    bindPanelFocus(this.popover, (target) => target.tagName === "INPUT");

    document.addEventListener("pointerdown", (event) => {
      const target = event.target as Node;
      if (!this.popover.contains(target) && !this.element.contains(target)) this.close();
    });
  }

  mount(): void {
    store.on("radio", () => this.render());
    radio.player.subscribe(() => this.render());
    audioPrefs.onChange(() => this.render());
    this.render();
  }

  close(): void {
    if (this.popover.contains(document.activeElement))
      (document.activeElement as HTMLElement).blur();
    this.popover.classList.add("hidden");
    this.element.setAttribute("aria-expanded", "false");
  }

  private toggle(): void {
    const open = this.popover.classList.toggle("hidden") === false;
    this.element.setAttribute("aria-expanded", String(open));
    // Mouse users keep walking with WASD.
    this.element.blur();
  }

  private render(): void {
    const desired = store.radio;
    this.element.classList.toggle("hidden", !desired.playing);
    if (!desired.playing) {
      this.close();
      return;
    }
    const local = radio.player.getState();
    const line = describePlayback(desired, local);
    const silent = audioPrefs.isMuted() || audioPrefs.radioVolume() === 0;

    this.label.textContent = `♪ ${desired.stationName}`;
    this.icon.textContent = line.action ? "⚠" : silent ? "🔇" : "🔊";
    this.element.title = desired.stationName;
    this.element.setAttribute("aria-label", `Radio: ${desired.stationName}. Volume and status`);
    this.element.classList.toggle("attention", !!line.action);

    this.popName.textContent = `♪ ${desired.stationName}`;
    this.popBy.textContent = desired.tunedBy ? `tuned by ${desired.tunedBy}` : "";
    this.popStatus.textContent = line.tone === "live" ? "LIVE ●" : line.text;
    this.popStatus.dataset.tone = line.tone;
    this.popAction.classList.toggle("hidden", !line.action);
    if (line.action) {
      this.popAction.textContent = line.action.label;
      this.popAction.setAttribute("aria-label", line.action.aria);
    }
  }
}
